
    import { defineComponent } from 'vue'
    import { AxiosResponse } from 'axios'
    import descriptor from './ResourceManagementDescriptor.json'
    import Tree from 'primevue/tree'
    import { IFolderTemplate } from '@/modules/managers/resourceManagement/ResourceManagement'
    import { downloadDirectFromResponseWithCustomName } from '@/helpers/commons/fileHelper'
    import ResourceManagementMetadataDialog from '@/modules/managers/resourceManagement/ResourceManagementMetadataDialog.vue'
    import ResourceManagementDetail from './ResourceManagementDetail.vue'
    import KnHint from '@/components/UI/KnHint.vue'
    import ResourceManagementCreateFolderDialog from './ResourceManagementCreateFolderDialog.vue'

    export default defineComponent({
        name: 'resource-management',
        components: { KnHint, ResourceManagementMetadataDialog, ResourceManagementCreateFolderDialog, ResourceManagementDetail, Tree },
        data() {
            return {
                descriptor,
                displayMetadataDialog: false,
                loading: false,
                nodes: [] as IFolderTemplate[],
                expandedKeys: {},
                selectedKeys: null,
                metadataKey: null,
                dirty: false,
                buttonsVisible: [],
                showHint: true,
                touched: false,
                selectedFolder: {} as IFolderTemplate,
                folderCreation: false,
                formVisible: false
            }
        },
        async created() {
            this.loadPage()
        },
        methods: {
            createFolder(folderName: string) {
                if (folderName && this.selectedFolder) {
                    let obj = {} as JSON
                    obj['key'] = '' + this.selectedFolder.key
                    obj['folderName'] = folderName
                    this.$http
                        .post(process.env.VUE_APP_API_PATH + `2.0/resources/folders`, obj, {
                            responseType: 'arraybuffer', // important...because we need to convert it to a blob. If we don't specify this, response.data will be the raw data. It cannot be converted to blob directly.

                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                        .then(() => {
                            this.$emit('folderCreated', true)
                        })
                        .catch((error) => {
                            this.$store.commit('setError', {
                                title: this.$t('common.error.saving'),
                                msg: this.$t(error)
                            })
                        })
                        .finally(() => {
                            this.loading = false
                            this.openCreateFolderDialog()
                            this.loadPage(this.showHint, this.formVisible)
                        })
                }
            },
            getCurrentFolderPath() {
                return this.selectedFolder ? this.selectedFolder.relativePath : undefined
            },
            getCurrentFolderKey() {
                return this.selectedFolder ? '' + this.selectedFolder.key : undefined
            },
            getButtonClass(node) {
                let visibility = ' kn-hide'
                if (this.buttonsVisible[node.key] && !node.edit) visibility = ''
                return 'p-button-text p-button-sm p-button-rounded p-button-plain p-p-0' + visibility
            },
            openCreateFolderDialog() {
                this.folderCreation = !this.folderCreation
            },
            toggleInput(node) {
                if (node.level > 0) {
                    if (node.edit && node.label !== node.edit) {
                        if (this.selectedFolder) {
                            let obj = {} as JSON
                            obj['key'] = this.selectedFolder.key
                            obj['folderName'] = node.label
                            this.loading = true
                            this.$http
                                .post(process.env.VUE_APP_API_PATH + `2.0/resources/folders/update`, obj, {
                                    responseType: 'arraybuffer', // important...because we need to convert it to a blob. If we don't specify this, response.data will be the raw data. It cannot be converted to blob directly.

                                    headers: {
                                        'Content-Type': 'application/json'
                                    }
                                })
                                .then(() => {
                                    delete node.edit
                                    this.$store.commit('setInfo', {
                                        title: this.$t('managers.resoruceManagement.renameFolder'),
                                        msg: this.$t('managers.resoruceManagement.folderRenamedSuccessfully')
                                    })
                                })
                                .catch((error) => {
                                    this.$store.commit('setError', {
                                        title: this.$t('managers.resoruceManagement.renameFolder'),
                                        msg: this.$t(error)
                                    })
                                })
                                .finally(() => {
                                    this.loading = false
                                })
                        }
                    } else node.edit = node.label
                }
            },
            addIcon(nodes) {
                for (var idx in nodes) {
                    let node = nodes[idx]
                    node.icon = 'far fa-folder'
                    if (node.children && node.children.length > 0) {
                        this.addIcon(node.children)
                    }
                }
            },
            loadPage(showHint?, formVisible?): void {
                this.loading = true
                this.showHint = showHint != undefined ? showHint : true
                this.formVisible = formVisible != undefined ? formVisible : false
                this.$http
                    .get(process.env.VUE_APP_API_PATH + `2.0/resources/folders`)
                    .then((response: AxiosResponse<any>) => {
                        let root = response.data.root[0]
                        root.label = 'HOME'
                        root.icon = 'pi pi-home'
                        root.disabled = true
                        this.addIcon(root.children)
                        this.nodes = response.data.root
                        this.loading = false
                    })
                    .finally(() => (this.loading = false))
            },
            openMetadataDialog(node): void {
                this.metadataKey = node.key
                this.displayMetadataDialog = !this.displayMetadataDialog
            },
            deleteFolder(node) {
                this.loading = true
                this.$http
                    .delete(process.env.VUE_APP_API_PATH + `2.0/resources/folders`, {
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        data: {
                            key: node.key
                        }
                    })
                    .then(() => {
                        this.loadPage()
                        this.$store.commit('setInfo', {
                            title: this.$t('common.toast.deleteTitle'),
                            msg: this.$t('common.toast.deleteSuccess')
                        })
                    })
                    .catch(() => {
                        this.$store.commit('setError', {
                            title: this.$t('common.toast.deleteTitle'),
                            msg: this.$t('common.toast.deleteFailed')
                        })
                    })
                    .finally(() => (this.loading = false))
            },
            downloadDirect(node) {
                this.loading = true
                let obj = {} as JSON
                obj['key'] = node.key
                this.$http
                    .post(process.env.VUE_APP_API_PATH + `2.0/resources/folders/download`, obj, {
                        responseType: 'arraybuffer', // important...because we need to convert it to a blob. If we don't specify this, response.data will be the raw data. It cannot be converted to blob directly.

                        headers: {
                            'Content-Type': 'application/json',
                            Accept: 'application/zip; charset=utf-8'
                        }
                    })
                    .then((response: AxiosResponse<any>) => {
                        downloadDirectFromResponseWithCustomName(response, this.selectedFolder.label + '.zip')
                    })
                    .finally(() => (this.loading = false))
            },
            setDirty(): void {
                this.dirty = true
            },
            showForm(functionality: IFolderTemplate) {
                /*             this.functionalityParentId = parentId */
                if (!this.touched) {
                    this.setSelected(functionality)
                } else {
                    this.$confirm.require({
                        message: this.$t('common.toast.unsavedChangesMessage'),
                        header: this.$t('common.toast.unsavedChangesHeader'),
                        icon: 'pi pi-exclamation-triangle',
                        accept: () => {
                            this.touched = false
                            this.setSelected(functionality)
                        }
                    })
                }
            },
            setSelected(functionality: IFolderTemplate) {
                this.selectedFolder = functionality
                this.formVisible = true
                this.showHint = false
            },
            showDeleteDialog(node) {
                this.$confirm.require({
                    message: this.$t('common.toast.deleteMessage'),
                    header: this.$t('common.toast.deleteConfirmTitle'),
                    icon: 'pi pi-exclamation-triangle',
                    accept: () => this.deleteFolder(node)
                })
            },
            switchToHint() {
                this.formVisible = false
                this.showHint = true

                this.expandedKeys = {}
                this.selectedKeys = null
                this.metadataKey = null
                this.dirty = false

                this.touched = false
                this.selectedFolder = {} as IFolderTemplate
            }
        }
    })
